import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import Modal from '@components/common/Modal';
import cn from 'classnames';
import { APP_CONFIG } from '@constants';
import { LOGOUT_SUCCESS, closeManagerCheck, setLoggingOut } from '@reducers/oauth';
import { LABEL, CUSTOM_MESSAGES } from '../../../veriables';
import EventEmitter from '@utility/eventSubscribers';
import { getObjectValue } from '@utility/helperFunction';
import { flowType } from '@utility/userInfo';
import { isEmpty } from 'lodash';
import { setOpenModalPopUp, setVoidCheckOpen, toggleNestedModifierFlag } from '@reducers/orders';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { TouchableButton } from '../../../components/common/Touchable/index';
import { urlSearchParams } from '../../../utility/utills';

// Constants
const PAYMENT_PATHS = [
  '/payment',
  '/stagepayment',
  '/powerPayment',
  '/normalPayment',
  '/presenter',
  '/fireboard',
];
const CUSTOM_ACTIVITY_EVENT = 'customUserActivity';
const USER_ACTIVITY_EVENTS = [
  'click',
  'dblclick',
  'touchstart',
  'touchmove',
  'touchend',
  'touchcancel',
  'keydown',
  CUSTOM_ACTIVITY_EVENT,
];

const InActivity = props => {
  const {
    // inActivityStatus,
    serverAppTheme,
    deviceInfo,
    stopPopUp,
    userInfo,
    checkVoid,
    setIsAssisstance,
    clearCart,
    removeSeat,
    clearAuthResponse,
    removePaymentCheck,
    updateChecksAfterResponse,
    setNotification,
    addChecks,
    // setInActivityStatus,
    logout,
    setSeat,
    closeManagerCheck,
    toggleNestedModifierFlag,
    isNestedModifier,
    setLoggingOut
  } = props;
 
  const [isOpen, setIsOpen] = useState(false);
  const [count, setCount] = useState(30);

  const inactivityTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  // Memoized flow type checks
  const flowTypes = useMemo(
    () => ({
      isServer: flowType() === 'serverApp',
      isTableTop: flowType() === 'tableTop',
      isKiosk: flowType() === 'kiosk',
    }),
    []
  );

  // Memoized device mode checks
  const deviceModes = useMemo(
    () => ({
      isDefaultStartMode: deviceInfo?.deviceGroup?.startMode === 0,
      isCounter: flowTypes.isServer && deviceInfo?.deviceGroup?.startMode === 1,
      isOrderPayMode:
        flowTypes.isTableTop && deviceInfo?.deviceGroup?.startMode === 1,
    }),
    [deviceInfo?.deviceGroup?.startMode, flowTypes]
  );

  // Helper to resolve interval values
  const resolveInterval = useCallback(
    (value, fallback = 0) =>
      typeof value === 'number' && value > 0 ? value : fallback,
    []
  );

  // Calculate timeout and countdown intervals
  const intervals = useMemo(() => {
    const authData = props?.auth || {};

    return {
      timeout: resolveInterval(authData?.timeoutInterval) || 0,
      countdown: resolveInterval(authData?.countdownInterval) || 0,
    };
  }, [props?.auth, deviceInfo, resolveInterval]);

  // Check for payment screen
  const checkPaymentScreen = () => {
    const typeParam = urlSearchParams('type');
    const screenParam = urlSearchParams('screenType');
    if (!typeParam && screenParam === String(1)) {
      return true;
    }
    if (
      typeParam === 'splitType' &&
      [9, 29, 30].includes(Number(screenParam))
    ) {
      return true;
    }
    return false;
  };

  // Check if timer should run
  const shouldRunTimer = useCallback(() => {
    if (
      !deviceModes.isDefaultStartMode ||
      intervals.timeout === 0 ||
      stopPopUp
    ) {
      return false;
    }
    //need to check for kiosk
    // if (!!deviceModes.isDefaultStartMode && flowTypes === 'kiosk') return false;

    const path = window.location.pathname;
    if (path.includes('/receipt')) {
      return checkPaymentScreen();
    }
    return (
      !path.includes('/checkIn') && !PAYMENT_PATHS.some(p => path.includes(p))
    );
  }, [deviceModes.isDefaultStartMode, intervals.timeout, stopPopUp]);

  // Get current check from session
  const getCurrentCheck = useCallback(
    () =>
      getObjectValue(
        () => JSON.parse(window.sessionStorage.getItem('seat')),
        []
      ),
    []
  );

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearInterval(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  // Start countdown timer
  const startCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }

    let remaining = intervals.countdown;
    setCount(remaining);

    countdownTimerRef.current = setInterval(() => {
      remaining -= 1;
      setCount(remaining);
      if (remaining <= 0) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
        signOut();
      }
    }, 1000);
  }, [intervals.countdown]);
  
  // Open inactivity popup
  const openPopup = useCallback(() => {
    if (!shouldRunTimer()) {
      clearAllTimers();
      return;
    }
    setIsOpen(true);
    // }, [shouldRunTimer, clearAllTimers, setInActivityStatus]);
  }, [shouldRunTimer, clearAllTimers]);

  // Reset inactivity timer
  const resetTimer = useCallback(() => {
    if (isOpen || !shouldRunTimer()) {
      return;
    }

    clearAllTimers();

    let elapsed = 0;
    const timeout = intervals.timeout;

    inactivityTimerRef.current = setInterval(() => {
      elapsed += 1;
      if (elapsed >= timeout) {
        clearInterval(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
        openPopup();
      }
    }, 1000);
  }, [isOpen, shouldRunTimer, clearAllTimers, intervals.timeout, openPopup]);

  // Handle stay button
  const handleStay = useCallback(() => {
    setIsOpen(false);
    clearAllTimers();
    setCount(intervals.countdown);
    resetTimer();
    // }, [clearAllTimers, intervals.countdown, resetTimer, setInActivityStatus]);
  }, [clearAllTimers, intervals.countdown, resetTimer]);

  // Add check helper
  const addCheck = useCallback(
    (items, callback) => {
      const check = getCurrentCheck();

      if (items.length === 0) {
        callback();
        return;
      }

      const dataToSend = {
        storeId: deviceInfo?.storeId,
        deviceId: deviceInfo?.deviceId,
        employeeId: Number(userInfo?.id),
        employeeCard: userInfo?.employeeCard,
        checkHeaderId: check?.id,
        stationId: check?.stationId,
        items,
        orderJson: JSON.stringify(items),
        longitude: '0',
        latitude: '0',
        geoLocation: '0',
      };

      addChecks(dataToSend, 'add')
        .then(res => {
          updateChecksAfterResponse(res);
          callback(res);
        })
        .catch(e => {
          setNotification({ type: 'danger', message: e.message });
        });
    },
    [
      deviceInfo,
      userInfo,
      addChecks,
      updateChecksAfterResponse,
      setNotification,
      getCurrentCheck,
    ]
  );

  // Cancel order check
  const cancelOrderCheck = useCallback(() => {
    props.setOpenModalPopUp(false);
    ['counterName', 'counterPhone', 'counterCountry'].forEach(key =>
      window.sessionStorage.removeItem(key)
    );

    const check = getCurrentCheck();
    const dataToSend = {
      employeeId: Number(userInfo?.id),
      storeId: deviceInfo.storeId,
      checkHeaderId: check?.id,
      deviceId: deviceInfo.deviceId,
    };

    if (flowTypes.isKiosk) {
      if (check?.open || check?.unPaidAmount > 0) {
        checkVoid(dataToSend);
      }
      setIsAssisstance(false);
      clearCart();
      removeSeat();
      clearAuthResponse();
      removePaymentCheck();
      props.navigate(`${APP_CONFIG.BASE_URL}/${flowType()}/landing`, {
        replace: true,
      });
    } else {
      checkVoid(dataToSend)
        .then(res => {
          updateChecksAfterResponse(res);
          const speedStatus = window.sessionStorage.getItem('speedStatus');

          if (deviceModes.isCounter || speedStatus) {
            window.sessionStorage.removeItem('speedStatus');
            props.navigate(`${APP_CONFIG.BASE_URL}/checkIn?step=1&logoutAfterRelaunchInterval=${Date.now()}`, { replace: true });
          } else {
            props.navigate(`${APP_CONFIG.BASE_URL}/${flowType()}/checks`, {
              replace: true,
            });
          }
        })
        .catch(e => {
          setNotification({ type: 'danger', message: e.message });
        });
    }
  }, [
    deviceInfo,
    userInfo,
    checkVoid,
    flowTypes.isKiosk,
    deviceModes.isCounter,
    getCurrentCheck,
    props,
    setIsAssisstance,
    clearCart,
    removeSeat,
    clearAuthResponse,
    removePaymentCheck,
    updateChecksAfterResponse,
    setNotification,
  ]);

  // Sign out logic
  const signOut = useCallback(() => {
    setIsOpen(false);
    setLoggingOut(true);

    if (isNestedModifier) toggleNestedModifierFlag(false);
    clearAllTimers();

    if (deviceInfo?.deviceGroup?.startMode === 2) {
      handleClose();
      return;
    }

    const cart = JSON.parse(window.sessionStorage.getItem('cart')) || [];
    const check = getCurrentCheck();
    const checkItems = check?.orders?.[0]?.items || [];

    // Get in-process items
    const inProcessItems =
      cart.filter(e => e.isProcessing)?.length > 0
        ? cart.filter(e => e.isProcessing)
        : checkItems
          .filter(v => v.itemType !== 'D' && v.pending && v.isProcessing)
          .map(v => {
            const orderJson = JSON.parse(v.orderJson);
            const modifierJson = JSON.parse(orderJson.modifierJson);
            return { ...v, ...orderJson, ...modifierJson };
          });

    const hasCheck = !isEmpty(check);
    const shouldCancelOrder =
      (deviceModes.isOrderPayMode ||
        flowTypes.isKiosk ||
        deviceModes.isCounter ||
        window.sessionStorage.getItem('speedStatus')) &&
      hasCheck;

    if (shouldCancelOrder) {
      if (
        deviceModes.isCounter ||
        window.sessionStorage.getItem('speedStatus')
      ) {
        addCheck(inProcessItems, cancelOrderCheck);
      } else {
        cancelOrderCheck();
      }
    } else if (flowTypes.isServer) {
      addCheck(inProcessItems, () => {
        const dataToSend = {
          latitude: '92',
          longitude: '92',
          deviceId: deviceInfo?.deviceId,
          employeeId: userInfo?.externalId,
          employeeCard: userInfo?.employeeCard,
        };
        props.navigate(`${APP_CONFIG.BASE_URL}/checkIn?step=1&logoutAfterRelaunchInterval=${Date.now()}`);
        logout(dataToSend);
        props.setVoidCheckOpen(false);
        closeManagerCheck();

        if (
          window.ReactNativeWebView &&
          (flowTypes.isServer || flowTypes.isTableTop)
        ) {
          EventEmitter.changeOrientation({ orientation: 'landscape' });
        }
      });
    } else {
      addCheck(inProcessItems, res => {
        setSeat(res).then(() => {
          props.navigate(`${APP_CONFIG.BASE_URL}/${flowType()}/checks`, {
            replace: true,
          });
        });
      });
    }

    if(!!window.sessionStorage.getItem("menuScrollTop")) {
      window.sessionStorage.removeItem("menuScrollTop");
    }
  }, [
    clearAllTimers,
    deviceInfo,
    userInfo,
    flowTypes,
    deviceModes,
    getCurrentCheck,
    addCheck,
    cancelOrderCheck,
    logout,
    closeManagerCheck,
    setSeat,
    props,
  ]);

  // Handle close for call center mode
  const handleClose = useCallback(() => {
    const check = getCurrentCheck();
    const dataToSend = {
      employeeId: Number(userInfo?.id),
      storeId: deviceInfo?.storeId,
      checkHeaderId: check?.id,
      deviceId: deviceInfo?.deviceId,
    };

    if (dataToSend?.checkHeaderId) {
      checkVoid(dataToSend)
        .then(res => {
          clearCart();
          updateChecksAfterResponse(res).then(() => {
            if (deviceInfo?.deviceGroup?.startMode === 2) {
              props.navigate(`${APP_CONFIG.BASE_URL}/callCenterError`);
              setIsOpen(false);
            } else {
              const type =
                !deviceInfo?.deviceGroup?.inputFields?.nameEnabled &&
                !deviceInfo?.deviceGroup?.inputFields?.phoneNumberEnabled;
              props.navigate(`${APP_CONFIG.BASE_URL}/${flowType()}/checks`, {
                state: { type: type ? 'checks' : 'order' },
                replace: true,
              });

              if (
                window.ReactNativeWebView &&
                (flowTypes.isServer || flowTypes.isTableTop)
              ) {
                EventEmitter.changeOrientation({ orientation: 'landscape' });
              }
            }
          });
        })
        .catch(e => {
          setNotification({ type: 'danger', message: e.message });
        });
    } else {
      props.navigate(`${APP_CONFIG.BASE_URL}/callCenterError`);
      setIsOpen(false);
    }
  }, [
    deviceInfo,
    userInfo,
    checkVoid,
    clearCart,
    updateChecksAfterResponse,
    setNotification,
    flowTypes,
    getCurrentCheck,
    props,
  ]);

  // Start/stop countdown when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      startCountdown();
    } else {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      setCount(intervals.countdown);
    }
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, [isOpen, intervals.countdown, startCountdown]);

  // Main effect: Setup activity listeners and timer
  useEffect(() => {
    const logoutChannel = new BroadcastChannel(LOGOUT_SUCCESS);
    logoutChannel.onmessage = () => {
      clearAllTimers();
      setIsOpen(false);
    };

    const activityHandler = () => {
      if (!isOpen && shouldRunTimer()) {
        resetTimer();
      }
    };

    USER_ACTIVITY_EVENTS.forEach(event =>
      document.addEventListener(event, activityHandler, { passive: true })
    );

    if (shouldRunTimer() && userInfo) {
      resetTimer();
    } else {
    if (isNestedModifier) toggleNestedModifierFlag(false);
      clearAllTimers();
    }

    return () => {
      logoutChannel.close();
      USER_ACTIVITY_EVENTS.forEach(event =>
        document.removeEventListener(event, activityHandler)
      );
      clearAllTimers();
    };
  // }, [inActivityStatus, intervals.timeout, intervals.countdown, isOpen, stopPopUp,
  //     userInfo, shouldRunTimer, resetTimer, clearAllTimers]);
    }, [intervals.timeout, intervals.countdown, isOpen, stopPopUp, userInfo, shouldRunTimer, resetTimer, clearAllTimers]);

  // Kiosk-specific UI helpers
  const kioskUI = useMemo(() => {
    const check = getCurrentCheck();
    const hasActiveOrder = check?.open || check?.unPaidAmount > 0;

    return {
      showCancelOrder: hasActiveOrder,
      buttonLabel: hasActiveOrder ? LABEL().CONTINUE_ORDER : LABEL().STAY,
      message: hasActiveOrder
        ? `${CUSTOM_MESSAGES().IN_ACTIVITY_MESSAGE} ${count} ${LABEL().SECONDS
        }. ${CUSTOM_MESSAGES().PLEASE_MAKE_SELECTION}`
        : `${CUSTOM_MESSAGES().IN_RECEIPT_ACTIVITY_MESSAGE} ${count} ${LABEL().SECONDS
        }. ${CUSTOM_MESSAGES().PLEASE_MAKE_SELECTION}`,
    };
  }, [count, getCurrentCheck]);

  return (
    <Modal
      isOpen={isOpen}
      isModalCenter
      style={{
        width: '450px',
        margin: 'auto',
        backgroundColor: serverAppTheme?.popUp_bg,
        border: serverAppTheme?.popupBorder,
        boxShadow: serverAppTheme?.popUp_box_shadow,
        borderRadius: '16px',
      }}
      noBackground
    >
      <div
        className="col-12 py-3 h-100"
        style={{ color: serverAppTheme?.primary_text }}
      >
        <div className="d-flex align-item-center justify-content-center">
          <i
            className="fa fa-history"
            fill={serverAppTheme?.iconcolor}
            aria-hidden="true"
          />
        </div>

        {(flowTypes.isServer || flowTypes.isTableTop) && (
          <div className="col-12 w-100 px-0 fw-400 text-center fs-18 py-3">
            {`${CUSTOM_MESSAGES().INACTIVITY_LOG_OUT} ${count} ${LABEL().SECONDS
              }`}
          </div>
        )}

        {flowTypes.isKiosk && (
          <div className="col-12 w-100 px-0 fw-600 text-center fs-26">
            {kioskUI.message}
          </div>
        )}

        <div className="col-12 w-100 row mx-0 py-2 px-0 justify-content-center align-items-center text-center">
          {/* {flowTypes.isKiosk && kioskUI.showCancelOrder && (
            <div className="col-6 p-1">
              <button
                className={cn('w-100 btn44')}
                style={{
                  backgroundColor: serverAppTheme?.OFF_Premise_Btn,
                  border: serverAppTheme?.cancleBtn_Border,
                  color: serverAppTheme?.cancleBtn_font,
                  borderRadius: '8px',
                }}
                type="button"
                onClick={signOut}
              >
                {LABEL().CANCEL_ORDER}
              </button>
            </div>
          )} */}

          {(flowTypes.isServer ||
            flowTypes.isTableTop ||
            deviceModes.isOrderPayMode) && (
              <div className="col-6 p-1">
                <TouchableButton
                  className={cn('w-100 btn44')}
                  style={{
                    backgroundColor: serverAppTheme?.OFF_Premise_Btn,
                    border: serverAppTheme?.cancleBtn_Border,
                    color: serverAppTheme?.cancleBtn_font,
                    borderRadius: '8px',
                  }}
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    signOut();
                  }}
                  rippleEffect={true}
                >
                  {deviceModes.isOrderPayMode
                    ? LABEL().CANCEL_ORDER
                    : LABEL().LOG_OUT}
                </TouchableButton>
              </div>
            )}

          <div className="col-6 p-1">
            <TouchableButton
              className={cn('w-100 btn44')}
              style={{
                backgroundColor: serverAppTheme?.secondaryBtn,
                color: serverAppTheme?.btn_font_color,
                borderRadius: '8px',
              }}
              type="button"
              onClick={e => {
                e.preventDefault();
                handleStay();
              }}
              rippleEffect={true}
            >
              {flowTypes.isKiosk ? kioskUI.buttonLabel : LABEL().STAY}
            </TouchableButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = state => ({
  isNestedModifier: state.orders.isNestedModifier,
  serverAppTheme: state.theme.serverAppTheme,
})

const mapDispatchToProps = dispatch =>
  bindActionCreators({ setOpenModalPopUp, closeManagerCheck, setVoidCheckOpen, toggleNestedModifierFlag, setLoggingOut}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(InActivity);